import { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

function SearchableDropdown({
  label,
  name,
  value,
  onChange,
  options = [],
  placeholder = "Search and select...",
  error,
  hint,
  disabled,
  className = "",
  labelClassName = "",
  onSelectComplete,
  onAdd,
  addLabel = "Add New",
  autoFocus = false,
  highAttention = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [openUpwards, setOpenUpwards] = useState(false);
  const [dropdownStyles, setDropdownStyles] = useState({ visibility: "hidden" });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const filteredOptions = options.filter((option) =>
    String(option?.label || "")
      .toLowerCase()
      .includes(searchTerm.toLowerCase()),
  );

  const selectedLabel = options.find((opt) => String(opt.value) === String(value))?.label || "";

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Use useLayoutEffect to calculate position before paint to prevent flickering and misplacement
  useLayoutEffect(() => {
    if (isOpen && buttonRef.current) {
      const updatePosition = () => {
        const rect = buttonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = 320; // Max height approximation
        const shouldOpenUpwards = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
        
        setOpenUpwards(shouldOpenUpwards);
        setDropdownStyles({
          position: "fixed",
          width: `${rect.width}px`,
          left: `${rect.left}px`,
          top: shouldOpenUpwards ? "auto" : `${rect.bottom + 4}px`,
          bottom: shouldOpenUpwards ? `${window.innerHeight - rect.top + 4}px` : "auto",
          zIndex: 9999,
          visibility: "visible",
        });
      };

      updatePosition();
      
      // Update position on resize or scroll to keep it attached to the button
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);

      return () => {
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
      };
    } else {
      setDropdownStyles({ visibility: "hidden" });
    }
  }, [isOpen]);

  const handleSelect = (selectedValue) => {
    onChange({ target: { name, value: selectedValue } });
    setIsOpen(false);
    setSearchTerm("");
    if (typeof onSelectComplete === "function") {
      onSelectComplete();
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter" && filteredOptions.length > 0) {
      e.preventDefault();
      handleSelect(filteredOptions[0].value);
    }
  };

  const dropdownMenu = isOpen && !disabled && ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      style={dropdownStyles}
      className={`overflow-hidden rounded-xl border border-slate-200 bg-white shadow-enterprise-hover dark:border-slate-800 dark:bg-slate-900 animate-in fade-in duration-200 ${
        openUpwards ? "slide-in-from-bottom-1" : "zoom-in-95"
      }`}
    >
      <div className="border-b border-slate-100 p-1.5 dark:border-slate-800">
        <input
          type="text"
          placeholder="Search options..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          className="w-full rounded-lg border-none bg-slate-50 px-2.5 py-1 text-xs text-slate-900 outline-none focus:ring-0 dark:bg-slate-800 dark:text-slate-100"
          autoFocus
        />
      </div>

      <div className="max-h-60 overflow-y-auto p-1 custom-scrollbar">
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={`flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-xs font-semibold transition-all ${
                String(value) === String(option.value)
                  ? "bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400"
                  : "text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
              }`}
            >
              <span>{option.label}</span>
              {String(value) === String(option.value) && (
                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          ))
        ) : (
          <div className="px-3 py-4 text-center text-xs font-medium text-slate-500">
            No options match your search.
          </div>
        )}
      </div>

      {onAdd && (
        <div className="border-t border-slate-100 p-1 dark:border-slate-800">
          <button
            type="button"
            onClick={() => {
              setIsOpen(false);
              onAdd();
            }}
            className="flex w-full items-center gap-1.5 rounded-lg bg-primary-50 px-2.5 py-1.5 text-left text-[10px] font-black uppercase tracking-tight text-primary-600 transition-all hover:bg-primary-100 dark:bg-primary-900/20 dark:hover:bg-primary-900/30"
          >
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
            </svg>
            {addLabel}
          </button>
        </div>
      )}
    </div>,
    document.body
  );

  return (
    <div className={`relative space-y-1 ${className}`.trim()}>
      {label && (
        <label
          className={`block text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 ${labelClassName}`.trim()}
        >
          {label}
        </label>
      )}

      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          id={`${name}-searchable-dropdown-toggle`}
          data-form-nav-field="true"
          data-form-nav-open-only="true"
          disabled={disabled}
          autoFocus={autoFocus}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={`flex w-full items-center justify-between rounded-lg border bg-white px-2 py-1 text-left text-xs text-slate-900 focus:outline-none transition-all focus:ring-2 dark:bg-slate-900 dark:text-slate-100 ${
            error
              ? "border-danger focus:border-danger focus:ring-danger/20"
              : highAttention
                ? "border-primary-500 ring-2 ring-primary-500/20 shadow-md shadow-primary-500/10 dark:border-primary-400 dark:focus:ring-primary-dark/20"
                : "border-slate-200 focus:border-primary-500 focus:ring-primary-500/20 dark:border-slate-800 dark:focus:border-primary-500 dark:focus:ring-primary-dark/20"
          } ${disabled ? "cursor-not-allowed bg-slate-50 text-slate-400 dark:bg-slate-800" : "cursor-pointer"}`.trim()}
        >
          <span className="truncate">{selectedLabel || placeholder}</span>
          <svg
            className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {dropdownMenu}
      </div>

      {error ? (
        <p className="mt-1 text-xs font-medium text-danger">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-brand-text-secondary dark:text-slate-400">{hint}</p>
      ) : null}
    </div>
  );
}

export default SearchableDropdown;
